import { proceduralRoomContent } from '../../content/floors/procedural/roomContent';
import { pickNpc } from '../../content/npcs/npcs';
import type { DungeonFloor, DungeonRoom, RoomType } from '../game/types';
import { discoverAdjacent } from './discover';
import {
  connectAdjacentRooms,
  generateConnectedCells,
  gridKey,
  selectSpecialCells,
} from './generator/grid';
import { createRng, shuffle } from './generator/random';
import { buildBalancedRoomDeck } from './generator/roomDistribution';

export interface GenerateFloorOptions {
  floorNumber: number;
  seed?: number;
  width?: number;
  height?: number;
  roomCount?: number;
}

/**
 * Seeded random floor with balance constraints independent of player stats:
 * exactly 1 SAFE/BOSS/TELEPORTER, bounded room categories and distant goals.
 */
export function generateFloor(options: GenerateFloorOptions): DungeonFloor {
  const floorNumber = options.floorNumber;
  const width = Math.max(2, options.width ?? 5);
  const height = Math.max(2, options.height ?? 5);
  const capacity = width * height;
  const roomCount = Math.max(3, Math.min(options.roomCount ?? 10 + floorNumber, capacity));
  const rng = createRng(options.seed ?? floorNumber * 9973 + 42);
  const { start, cells } = generateConnectedCells(width, height, roomCount, rng);
  const { boss, teleporter } = selectSpecialCells(cells, start, rng);

  const specialTypes = new Map<string, RoomType>([
    [gridKey(start), 'SAFE'],
    [gridKey(boss), 'BOSS'],
    [gridKey(teleporter), 'TELEPORTER'],
  ]);
  const randomPositions = shuffle(
    rng,
    cells.filter((position) => !specialTypes.has(gridKey(position))),
  );
  const roomDeck = buildBalancedRoomDeck(randomPositions.length, rng);
  const randomTypes = new Map(
    randomPositions.map((position, index) => [gridKey(position), roomDeck[index] as RoomType]),
  );
  const rooms: Record<string, DungeonRoom> = {};

  for (const position of cells) {
    const id = gridKey(position);
    const type = specialTypes.get(id) ?? randomTypes.get(id) ?? 'EMPTY';
    rooms[id] = {
      id,
      gridPos: { ...position },
      type,
      status: id === gridKey(start) ? 'EXPLORED' : 'UNEXPLORED',
      thumbnailKey: 'room_thumb',
      connections: [],
      ...proceduralRoomContent(type, floorNumber, {
        npc: type === 'NPC' ? pickNpc(rng) : undefined,
      }),
    };
  }

  connectAdjacentRooms(rooms);

  let floor: DungeonFloor = {
    floorNumber,
    floorName: `Mê Cung · Tầng ${floorNumber.toString().padStart(2, '0')}`,
    gridWidth: width,
    gridHeight: height,
    root: { ...start },
    rooms,
    playerRoomId: gridKey(start),
    objectiveText: 'Tìm ma trận dịch chuyển (boss tùy chọn)',
    safeRoomId: gridKey(start),
    bossRoomId: gridKey(boss),
    teleporterRoomId: gridKey(teleporter),
  };

  floor = discoverAdjacent(floor, floor.playerRoomId);
  return floor;
}

export { createRng } from './generator/random';
